import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { Pane, Tab, TabNavigation } from "evergreen-ui";
import AccountingScreenSwitch from "./AccountingScreenSwitch";
import AccountingContext from "../../contexts/accounting";
import ControlhubContext from "../../contexts/controlhub";
import useControlHub from "../../hooks/useControlHub";
import { FREE_PLAN_NAME, APP_URL } from "../../config";

const BASENAME = "/settings";

const SYSTEMS_NAME_MAP = {
  quickbooks: "QuickBooks®",
  netsuite: "NetSuite®",
};

const TABS_MAP = {
  account: "accounts",
  classes: "categories",
};

const getSettingsMenu = ({ system, currentCompanyPlan }) => {
  if (currentCompanyPlan === FREE_PLAN_NAME) return [];

  let systemTabs = [];
  if (system && system.active && system.integrated) {
    const tabs = system.tabs;
    for (const [tab, active] of Object.entries(tabs)) {
      if (active) {
        systemTabs.push({
          location: `${BASENAME}/${system.name}/${tab}`,
          name: tab in TABS_MAP ? TABS_MAP[tab] : tab,
        });
      }
    }
  } else {
    systemTabs.push({
      location: `${APP_URL}/settings/categories`,
      name: "Categories",
      isExternalLink: true,
    });
  }
  systemTabs = systemTabs.reverse();
  return [
    ...systemTabs.slice(0, -1),
    {
      location: `${BASENAME}/integrations`,
      name:
        system && system.active && system.integrated
          ? "Connected ✓"
          : "Connect",
    },
    ...systemTabs.slice(systemTabs?.length - 1),
  ];
};

const AccountingScreen = ({ currentTab, service, setLoadingState }) => {
  const { api } = useControlHub();
  const { currentCompanyPlan } = useContext(ControlhubContext);
  const location = useLocation();
  const [context, setContext] = useState({});
  const [accounting, setAccounting] = useState();
  const [system, setSystem] = useState();

  const getConfiguration = useCallback(() => {
    if (currentCompanyPlan === FREE_PLAN_NAME) return;
    api
      .getAccountingConfiguration()
      .then((data) => {
        setAccounting(data);
        setSystem();
      })
      .catch(() => {});
  }, [api, currentCompanyPlan]);

  useEffect(() => {
    currentCompanyPlan && getConfiguration();
  }, [getConfiguration, currentCompanyPlan]);

  const setSystemByName = useCallback(
    (name) => {
      if (name) {
        if (accounting && name in accounting.systems) {
          setSystem({
            ...accounting.systems[name],
            name,
          });
        }
      } else {
        setAccounting();
        setSystem();
      }
    },
    [accounting]
  );

  useEffect(() => {
    setContext({
      accounting,
      system,
      setSystem: setSystemByName,
      SYSTEMS_NAME_MAP,
    });
  }, [accounting, setSystemByName, system]);

  useEffect(() => {
    if (!accounting || system) {
      return;
    }
    const defaultSystemName = Object.keys(accounting?.systems || {}).find(
      (systemName) =>
        accounting.systems[systemName].active &&
        accounting.systems[systemName].integrated
    );
    if (defaultSystemName) {
      setSystemByName(defaultSystemName);
    }
  }, [accounting, setSystemByName, system]);

  const settingsTabs = useMemo(() => {
    const confTabs = (currentTab?.childs_tab || []).map((tab) => ({
      ...tab,
      name: tab?.title,
      location: tab.slug ? `${BASENAME}/${tab?.slug}` : `${tab.location}`,
    }));

    const localTabs = getSettingsMenu({ system, currentCompanyPlan });
    return [
      ...confTabs.slice(0, -1),
      ...localTabs,
      ...confTabs.slice(confTabs?.length - 1),
    ];
  }, [currentCompanyPlan, currentTab?.childs_tab, system]);

  return (
    <AccountingContext.Provider value={context}>
      <Pane paddingX={48} paddingY={16}>
        <TabNavigation margin={4}>
          {settingsTabs.map((tab) => (
            <Tab
              {...(tab.isExternalLink && { is: "a", href: tab.location })}
              {...(!tab.isExternalLink && { is: Link, to: tab.location })}
              isSelected={location.pathname === tab.location}
              key={`accounting section - ${tab.name}`}
              textTransform="capitalize"
            >
              {tab.name}
            </Tab>
          ))}
        </TabNavigation>
        <Pane background="white" marginY={16} paddingX={16} paddingY={16}>
          <AccountingScreenSwitch
            basename={BASENAME}
            service={service}
            setLoadingState={setLoadingState}
          />
        </Pane>
      </Pane>
    </AccountingContext.Provider>
  );
};

export default AccountingScreen;
